// Common measurements for template filtering
export const COMMON_MEASUREMENTS = ['/components', '/interfaces/counters', '/system', '/system/cpus', '/system/memory'];

export const MEASUREMENT_GROUPS = [
  {
    key: 'essentials',
    label: 'Recommended',
    tag: 'essential',
    subscriptionLabel: 'Keep updated',
    subscriptionAriaLabel: 'Get the latest updates to measurements recommended by Kentik'
  },
  {
    key: 'customSnmp',
    label: 'Custom',
    tag: 'custom_snmp',
    subscriptionLabel: 'Keep updated',
    subscriptionAriaLabel: 'Add new custom measurements to this profile when they are created'
  },
  { key: 'other', label: 'Additional' }
];

export const MEASUREMENT_SUBSCRIPTION_FIELDS = {
  everything: 'uiProperties.measurements.subscriptions.everything',
  essentials: 'uiProperties.measurements.subscriptions.essentials',
  customSnmp: 'uiProperties.measurements.subscriptions.customSnmp'
};

export const getMeasurementSubscriptionState = ({ settings, defaultToEssentials = false }) => {
  const tags = new Set(Array.isArray(settings.tags) ? settings.tags : []);

  return {
    everything: settings['run-only-listed-reports'] === false && !settings.measurements,
    essentials: defaultToEssentials || tags.has('essential'),
    customSnmp: tags.has('custom_snmp')
  };
};

export const omitSubscribedMeasurements = ({ settings, groups }) => {
  const subscribedTags = new Set(Array.isArray(settings.tags) ? settings.tags : []);
  const subscribedMeasurementNames = new Set(
    groups.flatMap((group) =>
      group.measurements
        .filter((measurement) => {
          const tags = measurement.get('tags');
          return Array.isArray(tags) && tags.some((tag) => subscribedTags.has(tag));
        })
        .map((measurement) => measurement.get('measurement'))
    )
  );

  if (settings.measurements) {
    settings.measurements = Object.fromEntries(
      Object.entries(settings.measurements).filter(([name]) => !subscribedMeasurementNames.has(name))
    );
  }

  return settings;
};

export const applyMeasurementSubscriptions = ({ settings, groups, subscriptions }) => {
  const subscribedGroups = groups.filter((group) => group.tag && subscriptions[group.key]);

  if (subscribedGroups.length > 0) {
    settings.tags = subscribedGroups.map((group) => group.tag);
  } else {
    delete settings.tags;
  }

  return omitSubscribedMeasurements({ settings, groups });
};

export const shouldDisableUnlistedMeasurement = ({
  name,
  settings,
  runOnlyListedReports,
  subscribedNames,
  everythingSubscribed
}) => {
  if (settings.measurements?.[name]) {
    return false;
  }
  if (runOnlyListedReports) {
    return !subscribedNames.has(name);
  }
  return !everythingSubscribed && subscribedNames.size > 0 && !subscribedNames.has(name);
};

export const applyAllMeasurements = (settings) => {
  settings['run-only-listed-reports'] = false;
  delete settings.tags;
  delete settings.measurements;
  return settings;
};

/**
 * Serializes the measurement selection contract sent to Ranger.
 * Bounded profiles collect only measurements resolved from tags or listed explicitly.
 * Everything + Keep updated is unbounded and collects all current and future measurements.
 */
export const serializeMeasurementSettings = ({ settings, groups, subscriptions, everythingSubscribed }) => {
  delete settings['opt-mode'];

  if (everythingSubscribed) {
    return applyAllMeasurements(settings);
  }

  settings['run-only-listed-reports'] = true;
  delete settings.tags;

  if (settings.measurements) {
    settings.measurements = Object.fromEntries(
      Object.entries(settings.measurements).filter(([, measurementSettings]) => measurementSettings?.enabled)
    );
  }

  return applyMeasurementSubscriptions({ settings, groups, subscriptions });
};

export const groupMeasurements = (measurements) => {
  const groups = MEASUREMENT_GROUPS.map((group) => ({ ...group, measurements: [] }));

  measurements.forEach((measurement) => {
    const tags = measurement.get('tags');
    const matchingGroup = groups.find((group) => group.tag && Array.isArray(tags) && tags.includes(group.tag));
    (matchingGroup || groups[groups.length - 1]).measurements.push(measurement);
  });

  return groups;
};
